import { Suspense } from "react";

import { useElements } from "@/hooks/useElementsProvider";
import { Elements } from "./Elements";
import { Skeleton } from "../ui/skeleton";
import { ElementDetails } from "@/lib/types";

export type Element = {
  id: string;
  parentId: string | null;
  childrenIds: string[];
  name: keyof typeof Elements;
  config: Record<string, any>;
};

export default function UIElement({
  element,
  id,
  isInGrid,
}: {
  element: Element;
  id: string;
  isInGrid: boolean;
}) {
  const { elements, showGreeting } = useElements();

  const Component = Elements[element.name] as React.ComponentType<any>;
  if (!Component) {
    // Unknown element name — e.g. a widget that has since been removed (like the
    // old WallpaperSwitcher) still sitting in a saved dashboard, including before
    // the cleanup migration has run (dev DBs never run it). Render nothing in
    // every environment: a visible error card here would break every legacy
    // dashboard. In dev we log instead so it's still discoverable.
    if (import.meta.env.DEV) {
      console.warn(`UIElement: unknown element "${element.name}" skipped`);
    }
    return null;
  }

  let childrenAreInGrid: boolean = false;
  const extraProps: Record<string, unknown> = {
    isInGrid,
  };
  if (element.name === "Grid") {
    childrenAreInGrid = true;
    extraProps.initialGrid = element.childrenIds.map(id => {
      const childDefinition = Elements[
        elements[id].name
      ] as React.ComponentType<any> & {
        details: ElementDetails;
      };

      const [w, h, x = 0, y = 0] =
        elements[id].config.grid ||
        (childDefinition
          ? Object.values(childDefinition.details.grid)[0]
          : [0, 0, 0, 0]);

      return {
        position: [x, y],
        size: [w, h],
        element: elements[id],
        ...(childDefinition ? {} : { notFound: true }),
      };
    });
  }

  if (
    (["Greeting", "Search"].includes(element.name) ||
      (element.name === "Time" &&
        elements[element.parentId!].name !== "Grid")) &&
    !showGreeting
  ) {
    return null;
  }

  // Components are lazy (see Elements.ts); grid items get a skeleton matching
  // the widget card look, structural elements just wait for their chunk.
  return (
    <Suspense
      fallback={
        isInGrid ? <Skeleton className="h-full w-full rounded-xl" /> : null
      }
    >
      <Component id={id} {...element.config} {...extraProps}>
        {element.childrenIds.map(childId => (
          <UIElement
            element={elements[childId]}
            key={childId}
            id={childId}
            isInGrid={childrenAreInGrid}
          />
        ))}
      </Component>
    </Suspense>
  );
}
